/**
 * Servicio de autenticación
 * Basado en AuthAF.cs
 */

import { AuthPayload, ApolearnUserData } from '../types';
import configService from './configService';
import axios from 'axios';

export class AuthService {
  private apolearnToken: string | null = null;
  private apolearnUserData: ApolearnUserData | null = null;
  
  // Valores por defecto (quemados) para cuando no hay token de LTI
  // Estos se usan para hacer llamadas a la API de Apolearn sin LTI
  //private readonly DEFAULT_EMAIL = 'lauraai@lidarit.com';
  //private readonly DEFAULT_USER_ID = 9761286;
  //private readonly DEFAULT_ROLE = 'Instructor'; // Role por defecto cuando no hay LTI
  //private readonly DEFAULT_FULL_NAME = 'Laura Valero';
  //private readonly DEFAULT_CONTEXT_ID = '9735165';
  
  // Credenciales por defecto para autenticación con Apolearn API
  // Se usan cuando no hay credenciales en variables de entorno y el proxy Lambda no funciona
  // NOTA: Estas credenciales deben ser las mismas que se usan en el proxy Lambda
  // Si el proxy Lambda no funciona, estas se usarán como fallback
  // TODO: Verificar con el usuario cuáles son las credenciales correctas
  /**
   * Procesa la autenticación desde la URL
   * Extrae parámetros de la URL y verifica el token
   */
  processAuth(): AuthPayload | null {
    try {
      const config = configService.getConfig();
      
      // Si no se usa apolearn, no se requiere autenticación
      if (!config.useApolearn) {
        return null;
      }

      const url = window.location.href;
      const parameters = this.parseUrlParameters(url);

      // Si no hay token, no es una entrada desde Apolearn
      if (!parameters.token) {
        return null;
      }

      const payload: AuthPayload = {
        token: parameters.token || undefined,
        refresh: parameters.refresh || undefined,
        user_id: parameters.user_id || undefined,
        roles: parameters.roles || undefined,
        context_id: parameters.context_id || undefined,
        email: parameters.email || undefined,
        full_name: parameters.full_name || undefined,
      };

      // Guardar en configService
      if (payload.token) {
        configService.setAccessToken(payload.token);
      }
      if (payload.refresh) {
        configService.setRefreshToken(payload.refresh);
      }
      if (payload.email) {
        configService.setUserInfo(payload.email, payload.full_name || '');
      }

      // Verificar validez del token
      this.verifyToken(payload.token!);

      // Si detecta Apolearn y tiene email, crear datos mínimos primero
      if (config.useApolearn && payload.email) {
        // Mapear role de LTI: "Learner" -> "student", "Instructor" -> "teacher"
        let mappedRole: string | null = null;
        if (payload.roles) {
          const roleLower = payload.roles.toLowerCase();
          if (roleLower.includes('learner') || roleLower.includes('student') || roleLower.includes('estudiante')) {
            mappedRole = 'student';
          } else if (roleLower.includes('instructor') || roleLower.includes('teacher') || roleLower.includes('docente') || roleLower.includes('profesor')) {
            mappedRole = 'teacher';
          } else {
            mappedRole = payload.roles; // Mantener el role original si no coincide
          }
        }
        
        // Crear datos mínimos desde LTI Launch (mientras se obtienen los completos)
        const minimalData: ApolearnUserData = {
          user: {
            id: parseInt(payload.user_id || '0') || 0,
            email: payload.email,
            username: payload.email.split('@')[0],
            firstname: payload.full_name?.split(' ')[0] || '',
            lastname: payload.full_name?.split(' ').slice(1).join(' ') || '',
            subscription: {
              current_term_start: 0,
              current_term_end: 0,
            },
            is_account_valid: true,
          },
          classrooms: [], // Vacío por ahora, se llenará cuando se obtengan datos completos
          role: mappedRole, // Guardar el role mapeado desde LTI
        };
        this.apolearnUserData = minimalData;
        
        // NO llamar getApolearnCompleteData aquí porque App.tsx lo hará
        // Solo crear datos mínimos y dejar que App.tsx maneje la carga completa
        // Esto evita llamadas duplicadas
      }

      return payload;
    } catch (error) {
      console.error('Error en processAuth:', error);
      return null;
    }
  }

  /**
   * Parsea los parámetros de una URL
   */
  private parseUrlParameters(url: string): Record<string, string> {
    const urlObj = new URL(url);
    const parameters: Record<string, string> = {};

    urlObj.searchParams.forEach((value, key) => {
      parameters[key] = decodeURIComponent(value);
    });

    return parameters;
  }

  /**
   * Verifica la validez del token JWT
   */
  verifyToken(token: string): boolean {
    try {
      if (!token || token.trim() === '') {
        console.error('El token no es válido: está vacío o nulo.');
        return false;
      }

      // Dividir el token en sus tres partes (Header, Payload, Signature)
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        console.error('El token no tiene el formato correcto.');
        return false;
      }

      // Decodificar el Payload (parte 2 del token)
      const payload = tokenParts[1];
      const decodedPayload = this.decodeBase64(payload);

      // Parsear el JSON del Payload
      const payloadData = JSON.parse(decodedPayload);

      // Verificar si el atributo 'iat' está presente
      if (payloadData.iat) {
        const fechaCreacionToken = new Date(payloadData.iat * 1000);
        const fechaActual = new Date();

        // Calcular la diferencia en segundos
        const diferenciaEnSegundos = (fechaActual.getTime() - fechaCreacionToken.getTime()) / 1000;

        // Verificar si el token es válido (no más de 5 minutos de antigüedad)
        if (diferenciaEnSegundos > 5 * 60) {
          console.error(
            `El token ha expirado. Diferencia en segundos: ${diferenciaEnSegundos.toFixed(2)}`
          );
          return false;
        } else {
          return true;
        }
      } else {
        console.error("El atributo 'iat' no existe o no es válido en el token.");
        return false;
      }
    } catch (error) {
      console.error('Error al verificar el token:', error);
      return false;
    }
  }

  /**
   * Decodifica Base64 con padding correcto
   */
  private decodeBase64(base64: string): string {
    // Agregar padding si es necesario
    let padded = base64;
    const padding = 4 - (base64.length % 4);
    if (padding < 4) {
      padded = base64.padEnd(base64.length + padding, '=');
    }

    try {
      const decoded = atob(padded);
      return decodeURIComponent(
        decoded
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
    } catch (error) {
      console.error('Error al decodificar Base64:', error);
      throw error;
    }
  }

  /**
   * Determinar si el usuario es docente
   */
  isDocente(roles?: string | null): boolean {
    if (!roles) return false;
    // "Undefined" se trata como estudiante (no docente)
    if (roles === 'Undefined' || roles === 'undefined') return false;
    const rolesLower = roles.toLowerCase();
    return rolesLower.includes('docente') || rolesLower.includes('teacher') || rolesLower.includes('profesor');
  }

  /**
   * Determinar si el usuario es estudiante
   */
  isEstudiante(roles?: string | null): boolean {
    if (!roles) return true; // Por defecto es estudiante
    // "Undefined" se trata como estudiante
    if (roles === 'Undefined' || roles === 'undefined') return true;
    const rolesLower = roles.toLowerCase();
    return rolesLower.includes('estudiante') || rolesLower.includes('student') || rolesLower.includes('alumno');
  }

  /**
   * Obtener rol del usuario desde el payload de autenticación
   */
  getUserRole(): string | null {
    const authPayload = this.processAuth();
    return authPayload?.roles || null;
  }

  /**
   * Obtener datos completos de Apolearn del usuario
   */
  getApolearnUserData(): ApolearnUserData | null {
    return this.apolearnUserData;
  }

  /**
   * Obtener el context_id (classroom_id) del usuario desde la URL LTI.
   * Solo se obtiene de Apolearn/LTI; sin token no hay valor por defecto.
   */
  getContextId(): string | null {
    const urlParams = this.parseUrlParameters(window.location.href);
    return urlParams.context_id || null;
  }

  /**
   * SIMULACIÓN DE DATOS DE APOLEARN - SOLO PARA PRUEBAS
   * 
   * Esta función simula que el usuario obtuvo el nivel A1.1 de Apolearn por primera vez.
   * Los datos se guardan en memoria en `this.apolearnUserData`.
   * 
   * Para usar: authService.simulateApolearnA1_1('tu-email@ejemplo.com')
   * 
   * Para borrar los datos simulados: authService.clearSimulatedApolearnData()
   * 
   * IMPORTANTE: Estos datos solo existen en memoria durante la sesión del navegador.
   * Si recargas la página, los datos simulados se perderán.
   * 
   * @param email - Email del usuario para simular
   * @param firstName - Nombre del usuario (opcional, por defecto "Test")
   * @param lastName - Apellido del usuario (opcional, por defecto "User")
   */
  simulateApolearnA1_1(
    email: string = 'test@example.com',
    firstName: string = 'Test',
    lastName: string = 'User'
  ): void {
    const simulatedData: ApolearnUserData = {
      user: {
        id: 999999, // ID simulado
        email: email,
        username: email.split('@')[0],
        firstname: firstName,
        lastname: lastName,
        subscription: {
          current_term_start: Math.floor(Date.now() / 1000) - 86400 * 30, // Hace 30 días
          current_term_end: Math.floor(Date.now() / 1000) + 86400 * 365, // En 1 año
        },
        is_account_valid: true,
      },
      classrooms: [
        {
          id: 1,
          name: 'A1.1 - Francés Básico',
          description: 'Clase de nivel A1.1 simulada para pruebas',
          modules: [],
          statistics: {
            timespent: 0,
            progress: 0,
            satisfied: 0,
            course_number: 1, // 1 = A1.1 según el mapeo en academicService
          },
        },
      ],
    };

    this.apolearnUserData = simulatedData;
    
    // También actualizar configService con los datos simulados
    try {
      if (configService) {
        configService.setUserInfo(
          email,
          `${firstName} ${lastName}`,
          999999, // apolearn_user_id simulado
          firstName,
          lastName
        );
      }
    } catch (error) {
      console.error('[AuthService] Error al actualizar configService:', error);
    }

  }

  /**
   * BORRAR DATOS SIMULADOS DE APOLEARN
   * 
   * Esta función borra los datos simulados de Apolearn que se crearon con
   * `simulateApolearnA1_1()`.
   * 
   * Los datos se guardan en memoria en `this.apolearnUserData`.
   * Al llamar esta función, se establece `this.apolearnUserData = null`.
   * 
   * Para usar: authService.clearSimulatedApolearnData()
   * 
   * IMPORTANTE: Si los datos son reales (no simulados), esta función también los borrará.
   * Usa con precaución.
   */
  clearSimulatedApolearnData(): void {
    const hadData = this.apolearnUserData !== null;
    this.apolearnUserData = null;
    
    // Datos borrados
  }

  /**
   * Obtener URL base para peticiones a Apolearn
   * Usa proxy Lambda si está configurado (tanto en desarrollo como en producción)
   * Si no hay proxy Lambda, usa proxy de Vite en desarrollo o endpoint directo en producción
   */
  private getApolearnBaseUrl(): string {
    const isDev = import.meta.env.DEV;
    const proxyEndpoint = import.meta.env.VITE_APOLEARN_PROXY_ENDPOINT;
    
    // PRIORIDAD: Si hay proxy Lambda configurado, usarlo (tanto en dev como en prod)
    // El proxy Lambda maneja las credenciales automáticamente
    if (proxyEndpoint) {
      // Asegurar que no termine en barra para evitar dobles barras
      return proxyEndpoint.endsWith('/') ? proxyEndpoint.slice(0, -1) : proxyEndpoint;
    }
    
    // En desarrollo sin proxy Lambda, usar proxy de Vite para evitar CORS
    if (isDev) {
      return '/api/apolearn';
    }
    
    // Fallback: endpoint directo (puede fallar por CORS)
    const apiEndpoint = import.meta.env.VITE_APOLEARN_API_ENDPOINT || '';
    return apiEndpoint.endsWith('/') ? apiEndpoint.slice(0, -1) : apiEndpoint;
  }

  /**
   * Helper para extraer el resultado de la respuesta de Apolearn
   * Sigue la misma lógica que _make_request en Python (apolearn_client.py)
   * La API de Apolearn devuelve: {"status": 0, "result": [...]}
   * Extraer el campo 'result' si existe
   */
  private extractResult(data: any): any {
    if (data && typeof data === 'object' && 'result' in data) {
      return data.result;
    }
    return data;
  }

  /**
   * Autenticar con Apolearn API
   * En desarrollo puede usar credenciales locales si están disponibles (VITE_APOLEARN_USERNAME, VITE_APOLEARN_PASSWORD)
   * En producción, el proxy Lambda maneja las credenciales automáticamente
   */
  private async authenticateWithApolearn(): Promise<string> {
    const apiEndpoint = this.getApolearnBaseUrl();
    const isDev = import.meta.env.DEV;
    const proxyEndpoint = import.meta.env.VITE_APOLEARN_PROXY_ENDPOINT;
    
    // Determinar si estamos usando el proxy Lambda (tanto en dev como en prod)
    const isUsingLambdaProxy = !!proxyEndpoint;
    
    let username: string | undefined;
    let password: string | undefined;
    
    // Variables de entorno en desarrollo (opcional, solo si NO se usa proxy Lambda)
    if (isDev && !isUsingLambdaProxy) {
      username = import.meta.env.VITE_APOLEARN_USERNAME;
      password = import.meta.env.VITE_APOLEARN_PASSWORD;
    }

    if (!apiEndpoint) {
      throw new Error('Endpoint de Apolearn no configurado');
    }

    // Construir request body con credenciales
    // Si estamos usando el proxy Lambda, NO enviar credenciales (el proxy las maneja automáticamente)
    // Si NO estamos usando el proxy Lambda, enviar credenciales manualmente
    const requestBody: any = {};
    
    if (isUsingLambdaProxy) {
      // Proxy Lambda maneja las credenciales automáticamente - NO enviar nada
      // El requestBody queda vacío {}
    } else {
      // Sin proxy Lambda, necesitamos enviar credenciales manualmente
      const isPlaceholderUsername = username && (username.includes('your_username') || username.includes('example') || username.trim() === '');
      const isPlaceholderPassword = password && (password.includes('your_password') || password.includes('example') || password.trim() === '');
      
      // Usar credenciales de variables de entorno si están disponibles
      if (username && password && !isPlaceholderUsername && !isPlaceholderPassword) {
        requestBody.username = username;
        requestBody.password = password;
      } else if (username && password) {
        // Incluso si son placeholders, enviarlas (el servidor requiere el parámetro)
        requestBody.username = username;
        requestBody.password = password;
      } else {
        // Si no hay credenciales en variables de entorno, no enviar credenciales
        // El proxy Lambda debería manejarlas automáticamente
        // requestBody se deja vacío
      }
    }
    
    try {
      const url = `${apiEndpoint}/auth.gettoken`;
      const body = Object.keys(requestBody).length > 0 ? requestBody : {};
      const response = await axios.post(
        url,
        body,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      // Si response.data es un string, intentar parsearlo
      if (typeof response.data === 'string') {
        try {
          const parsed = JSON.parse(response.data);
          (response as any).data = parsed;
        } catch (e) {
          // Ignorar error de parsing
        }
      }
      
      // Verificar si hay un error en la respuesta
      if (response.data && typeof response.data === 'object') {
        if ('status' in response.data && response.data.status !== 0) {
          const errorMessage = response.data.message || 'Error desconocido en autenticación';
          console.error('[AuthService] ❌ Error de autenticación:', errorMessage);
          throw new Error(`Error en autenticación de Apolearn: ${errorMessage}`);
        }
      }

      // Manejar respuesta como en Python: extraer 'result' si existe
      let result = this.extractResult(response.data);
      
      // El token puede venir directamente como string o en un objeto
      if (typeof result === 'string') {
        this.apolearnToken = result;
        return this.apolearnToken;
      } else if (result && typeof result === 'object') {
        if ('token' in result && typeof result.token === 'string') {
          this.apolearnToken = result.token;
          return this.apolearnToken || '';
        } else if ('result' in result && typeof result.result === 'string') {
          this.apolearnToken = result.result;
          return this.apolearnToken || '';
        }
      }

      console.error('[AuthService] ❌ Estructura de respuesta inesperada');
      throw new Error('Error en autenticación de Apolearn - estructura de respuesta inesperada');
    } catch (error: any) {
      if (error.response) {
        // El servidor respondió con un código de estado fuera del rango 2xx
        const errorData = error.response.data;
        const errorMessage = errorData?.message || error.response.statusText || 'Error desconocido';
        console.error('[AuthService] ❌ Error al autenticar:', errorData || error.response.statusText);
        throw new Error(`Error en autenticación de Apolearn: ${errorMessage}`);
      }
      console.error('[AuthService] ❌ Error al autenticar:', error.message || error);
      throw error;
    }
  }

  /**
   * Obtener token de Apolearn API
   * Este token se obtiene haciendo login con username/password a /auth.gettoken
   * NO es el token de LTI Launch (ese solo identifica que viene desde Apolearn)
   * 
   * Flujo correcto:
   * 1. Token de LTI Launch viene en URL → identifica que es Apolearn
   * 2. Con email de LTI, hacer login a Apolearn API (username/password) → obtener auth_token
   * 3. Usar ese auth_token para todas las llamadas a Apolearn API
   */
  private async getApolearnToken(): Promise<string> {
    // Verificar si Apolearn está habilitado
    const config = configService.getConfig();
    if (!config.useApolearn) {
      throw new Error('Apolearn no está habilitado en la configuración');
    }
    
    // Si ya tenemos el token de autenticación de Apolearn, usarlo
    if (this.apolearnToken) {
      return this.apolearnToken;
    }
    
    // Si no hay token, autenticar con Apolearn API usando username/password
    // El proxy Lambda maneja las credenciales automáticamente
    return await this.authenticateWithApolearn();
  }

  /**
   * Obtener usuario de Apolearn por ID
   * Endpoint: GET /users/get/{user_id}
   * Estructura esperada: {"status": 0, "result": {"success": true, "users": [{...}]}}
   */
  private async getApolearnUserById(userId: number): Promise<any> {
    const apiEndpoint = this.getApolearnBaseUrl();
    const token = await this.getApolearnToken();

    try {
      const url = `${apiEndpoint}/users/get/${userId}`;
      const queryParams = {
        auth_token: token,
      };
      
      const response = await axios.get(
        url,
        {
          params: queryParams,
        }
      );
      
      if (response.data && typeof response.data === 'object') {
        if ('status' in response.data && response.data.status !== 0) {
          const errorMessage = response.data.message || 'Error desconocido';
          console.error('[AuthService] ❌ Error en get:', errorMessage);
          throw new Error(`Error al obtener usuario: ${errorMessage}`);
        }
      }

      let result = response.data;
      if (result && typeof result === 'object' && 'result' in result) {
        result = result.result;
      }
      
      if (result && typeof result === 'object') {
        if ('users' in result && Array.isArray(result.users) && result.users.length > 0) {
          console.log('[AuthService] 📋 Datos de usuario desde Apolearn (getbyid):', result.users[0]);
          return result.users[0];
        }
        if ('user' in result) {
          console.log('[AuthService] 📋 Datos de usuario desde Apolearn (getbyid):', result.user);
          return result.user;
        }
        if (result.id && (result.firstname !== undefined || result.lastname !== undefined)) {
          console.log('[AuthService] 📋 Datos de usuario desde Apolearn (getbyid):', result);
          return result;
        }
      }

      console.error('[AuthService] ❌ Usuario no encontrado - estructura inesperada');
      throw new Error('Usuario no encontrado en Apolearn - estructura de respuesta inesperada');
    } catch (error: any) {
      console.error('[AuthService] ❌ Error al obtener usuario por ID:', error.message || error);
      throw error;
    }
  }

  /**
   * Obtener usuario de Apolearn por email (fallback si no hay user_id)
   * Sigue la misma lógica que el cliente Python apolearn_client.py
   * Estructura esperada: {"status": 0, "result": {"success": true, "users": [{...}]}}
   */
  private async getApolearnUserByEmail(email: string): Promise<any> {
    const apiEndpoint = this.getApolearnBaseUrl();
    const token = await this.getApolearnToken();

    try {
      const url = `${apiEndpoint}/users/getbyemail/${encodeURIComponent(email)}`;
      
      const response = await axios.get(
        url,
        {
          params: {
            auth_token: token,
          },
        }
      );
      
      if (response.data && typeof response.data === 'object') {
        if ('status' in response.data && response.data.status !== 0) {
          const errorMessage = response.data.message || 'Error desconocido';
          console.error('[AuthService] ❌ Error en getbyemail:', errorMessage);
          throw new Error(`Error al obtener usuario: ${errorMessage}`);
        }
      }

      let result = response.data;
      if (result && typeof result === 'object' && 'result' in result) {
        result = result.result;
      }
      
      if (result && typeof result === 'object') {
        if ('users' in result && Array.isArray(result.users) && result.users.length > 0) {
          const user = result.users[0];
          console.log('[AuthService] 📋 Datos de usuario desde Apolearn (getbyemail):', user);
          return user;
        }
        
        if ('user' in result) {
          const user = result.user;
          console.log('[AuthService] 📋 Datos de usuario desde Apolearn (getbyemail):', user);
          return user;
        }
        
        if (result.id && (result.firstname !== undefined || result.lastname !== undefined)) {
          console.log('[AuthService] 📋 Datos de usuario desde Apolearn (getbyemail):', result);
          return result;
        }
      }

      console.error('[AuthService] ❌ Usuario no encontrado - estructura inesperada');
      throw new Error('Usuario no encontrado en Apolearn - estructura de respuesta inesperada');
    } catch (error: any) {
      console.error('[AuthService] ❌ Error al obtener usuario:', error.message || error);
      throw error;
    }
  }

  /**
   * Obtener información completa del estudiante desde Apolearn
   * Acepta tanto user_id como email
   * Retorna información unificada del usuario con datos completos de Apolearn:
   * - nivel (levels de las clases)
   * - classroom_ids
   * - role
   * - información completa del usuario y sus clases (syllabus, módulos, estadísticas, etc.)
   */
  async getStudentCompleteInfo(options: { user_id?: number; email?: string }): Promise<any> {
    try {
      // Verificar si Apolearn está habilitado
      const config = configService.getConfig();
      if (!config.useApolearn) {
        throw new Error('Apolearn no está habilitado en la configuración');
      }
      
      // Verificar si hay token de LTI en la URL
      const urlParams = this.parseUrlParameters(window.location.href);
      const hasLtiToken = !!urlParams.token;
      
      // Determinar email y user_id a usar (vienen de LTI o del formulario de correo en URL externa)
      const emailToUse = options.email;
      const userIdToUse = options.user_id;

      // Sin token LTI se requiere email o user_id (se pide en la URL externa; si no es Apolearn no se permite)
      if (!emailToUse && (!userIdToUse || userIdToUse <= 0)) {
        throw new Error('Se requiere user_id o email para obtener información del estudiante');
      }
      if (!emailToUse && (!userIdToUse || userIdToUse <= 0)) {
        throw new Error('Se requiere user_id o email para obtener información del estudiante');
      }

      // Usar getApolearnCompleteData que ya obtiene toda la información de Apolearn
      // y construye correctamente apolearnUserData
      const apolearnData = await this.getApolearnCompleteData(
        emailToUse || '',
        userIdToUse
      );

      // Obtener role del usuario desde Apolearn
      let userRole: string | null = null;
      try {
        const userId = apolearnData.user.id;
        const user = await this.getApolearnUserById(userId);
        userRole = user.role || user.roles || user.user_role || null;
      } catch {
        // Role no disponible desde Apolearn
      }

      // Extraer classroom_ids de los classrooms
      const classroomIds = apolearnData.classrooms.map((c) => c.id).filter((id) => id !== undefined);

      // Extraer niveles de las clases (puede haber múltiples niveles)
      const levels = apolearnData.classrooms
        .flatMap((c) => c.levels || [])
        .filter((level) => level !== undefined && level !== null);

      // Estructurar respuesta unificada con toda la información de Apolearn
      const userDataComplete = {
        user_info: {
          user: {
            id: apolearnData.user.id,
            email: apolearnData.user.email,
            username: apolearnData.user.username,
            firstname: apolearnData.user.firstname,
            lastname: apolearnData.user.lastname,
            role: userRole,
            subscription: apolearnData.user.subscription,
            is_account_valid: apolearnData.user.is_account_valid,
          },
          classrooms: apolearnData.classrooms.map((c) => ({
            id: c.id,
            name: c.name,
            description: c.description,
            levels: c.levels,
            info: c.info,
            modules: c.modules,
            statistics: c.statistics,
            classroom_statistics: c.classroom_statistics,
            students: c.students,
            teachers: c.teachers,
            admins: c.admins,
          })),
          // Información agregada para facilitar el acceso
          classroom_ids: classroomIds,
          levels: [...new Set(levels)], // Niveles únicos
          role: userRole,
        },
      };

      return userDataComplete;
    } catch (error: any) {
      console.error('[AuthService] ❌ Error al obtener información completa del estudiante:', error.message || error);
      throw error;
    }
  }

  /**
   * Obtener clases del usuario de Apolearn
   */
  private async getApolearnUserClassrooms(userId: number): Promise<any[]> {
    const apiEndpoint = this.getApolearnBaseUrl();
    const token = await this.getApolearnToken();

    try {
      const url = `${apiEndpoint}/users/getclassrooms/${userId}`;
      const queryParams = {
        auth_token: token,
        // api_key se agregará automáticamente en la Lambda
      };
      
      // Apolearn requiere TANTO api_key COMO auth_token
      // El api_key se manejará en la Lambda desde variables de entorno
      const response = await axios.get(
        url,
        {
          params: queryParams,
        }
      );

      let result = this.extractResult(response.data);
      let classrooms: any[] = [];
      if (Array.isArray(result)) {
        classrooms = result;
      } else if (result && typeof result === 'object') {
        if ('classrooms' in result && Array.isArray(result.classrooms)) {
          classrooms = result.classrooms;
        } else if ('result' in result && Array.isArray(result.result)) {
          classrooms = result.result;
        }
      }
      
      // Extraer y mostrar los classroom_id
      const classroomIds = classrooms.map((c: any) => c.id || c.classroom_id || c.classroomId).filter((id: any) => id !== undefined);
      
      // Extraer y mostrar los niveles de cada classroom
      const classroomsWithLevels = classrooms.map((c: any) => {
        const levels = c.levels || [];
        const levelNames = levels.map((l: any) => l?.name || l?.id).filter((n: any) => n !== undefined);
        return {
          id: c.id,
          name: c.name,
          levels: levels,
          levelNames: levelNames
        };
      });
      
      return classrooms;
    } catch (error: any) {
      console.error('[AuthService] ❌ Error al obtener clases:', error.message || error);
      return [];
    }
  }

  /**
   * Obtener módulos y actividades de una clase
   */
  private async getApolearnClassroomSyllabus(classroomId: number): Promise<any[]> {
    const apiEndpoint = this.getApolearnBaseUrl();
    const token = await this.getApolearnToken();

    try {
      // Apolearn requiere TANTO api_key COMO auth_token
      // El api_key se manejará en la Lambda desde variables de entorno
      const response = await axios.get(
        `${apiEndpoint}/classrooms/syllabus/${classroomId}`,
        {
          params: {
            auth_token: token,
            // api_key se agregará automáticamente en la Lambda
          },
        }
      );

      let result = this.extractResult(response.data);
      
      let syllabus: any[] = [];
      if (Array.isArray(result)) {
        syllabus = result;
      } else if (result && typeof result === 'object') {
        if ('syllabus' in result) {
          syllabus = Array.isArray(result.syllabus) ? result.syllabus : [];
        } else if ('result' in result) {
          syllabus = Array.isArray(result.result) ? result.result : [];
        }
      }
      
      return syllabus;
    } catch (error: any) {
      console.error('[AuthService] ❌ Error al obtener syllabus:', error.message || error);
      return [];
    }
  }

  /**
   * Obtener estadísticas del usuario de Apolearn
   */
  private async getApolearnUserStatistics(userId: number): Promise<any[]> {
    const apiEndpoint = this.getApolearnBaseUrl();
    const token = await this.getApolearnToken();

    try {
      const url = `${apiEndpoint}/statistics/user/${userId}`;
      
      const response = await axios.get(
        url,
        {
          params: {
            auth_token: token,
          },
        }
      );

      // Imprimir respuesta completa de getApolearnUserStatistics
      console.log('[AuthService] 📥 RESPUESTA COMPLETA de statistics/user/{user_id}:', JSON.stringify(response.data, null, 2));
      
      let result = this.extractResult(response.data);
      
      let statistics: any[] = [];
      if (Array.isArray(result)) {
        statistics = result;
      } else if (result && typeof result === 'object') {
        if ('statistics' in result && Array.isArray(result.statistics)) {
          statistics = result.statistics;
        } else if ('result' in result && Array.isArray(result.result)) {
          statistics = result.result;
        }
      }
      
      return statistics;
    } catch (error: any) {
      console.error('[AuthService] ❌ Error al obtener estadísticas:', error.message || error);
      return [];
    }
  }

  /**
   * Obtener toda la información estructurada de Apolearn para un usuario
   */
  async getApolearnCompleteData(email: string, userId?: number): Promise<ApolearnUserData> {
    try {
      // Verificar si Apolearn está habilitado
      const config = configService.getConfig();
      if (!config.useApolearn) {
        throw new Error('Apolearn no está habilitado en la configuración');
      }
      
      // Verificar si hay token de LTI en la URL
      const urlParams = this.parseUrlParameters(window.location.href);
      const hasLtiToken = !!urlParams.token;
      const hasTacheId = !!urlParams.tache_id;
      
      // Determinar email y user_id a usar
      let emailToUse = email;
      let userIdToUse = userId;
      
      // Sin token LTI se requiere email o user_id (formulario de correo en URL externa; si no es Apolearn no se permite)
      if (!emailToUse?.trim() && (!userIdToUse || userIdToUse <= 0)) {
        throw new Error('Se requiere email o user_id para obtener datos de Apolearn');
      }

      // 1. Obtener usuario
      // PRIORIDAD: Si tenemos user_id, usar /users/get/{user_id}
      // Si no, usar /users/getbyemail/{email} como fallback
      let user: any;
      let finalUserId: number;
      
      if (userIdToUse && userIdToUse > 0) {
        // Usar endpoint /users/get/{user_id} (más eficiente y directo)
        user = await this.getApolearnUserById(userIdToUse);
        finalUserId = userIdToUse;
      } else {
        // Fallback: usar /users/getbyemail/{email}
        user = await this.getApolearnUserByEmail(emailToUse);
        finalUserId = user.id;
      }
      
      // 2. Obtener clases del usuario - GET /users/getclassrooms/{user_id}
      const classroomsData = await this.getApolearnUserClassrooms(finalUserId);

      // 3. Obtener estadísticas - GET /statistics/user/{user_id}
      const statisticsData = await this.getApolearnUserStatistics(finalUserId);

      // Crear mapa de estadísticas, asegurando que los IDs sean del mismo tipo (number)
      const statisticsMap = new Map(
        statisticsData.map((stat: any) => {
          const classroomId = typeof stat.classroom_id === 'string' 
            ? parseInt(stat.classroom_id, 10) 
            : stat.classroom_id;
          return [classroomId, stat];
        })
      );

      // 4. Para cada clase, obtener información completa:
      //    - GET /classrooms/get/{classroom_id} - Información básica
      //    - GET /classrooms/syllabus/{classroom_id} - Módulos y actividades
      //    - GET /classrooms/students/{classroom_id} - Estudiantes
      //    - GET /classrooms/teachers/{classroom_id} - Profesores
      //    - GET /classrooms/admins/{classroom_id} - Administradores
      //    - GET /statistics/classroom/{classroom_id} - Estadísticas de la clase
      const classrooms = await Promise.all(
        classroomsData.map(async (classroom: any) => {
          const classroomId = typeof classroom.id === 'string' 
            ? parseInt(classroom.id, 10) 
            : classroom.id;

          // Obtener toda la información de la clase en paralelo
          const [
            classroomInfo,
            syllabus,
            students,
            teachers,
            admins,
            classroomStats,
            userClassroomStats
          ] = await Promise.all([
            this.getApolearnClassroom(classroomId).catch(() => null),
            this.getApolearnClassroomSyllabus(classroomId),
            this.getApolearnClassroomStudents(classroomId).catch(() => []),
            this.getApolearnClassroomTeachers(classroomId).catch(() => []),
            this.getApolearnClassroomAdmins(classroomId).catch(() => []),
            this.getApolearnClassroomStatistics(classroomId).catch(() => []),
            this.getApolearnUserClassroomStatistics(classroomId).catch(() => null)
          ]);
          const moduleDetailsPromises = syllabus.map((module: any) => this.getApolearnModule(module.id).catch(() => null));
          const moduleDetailsResults = await Promise.all(moduleDetailsPromises);

          const modules = syllabus.map((module: any, index: number) => {
            const moduleDetails = moduleDetailsResults[index];
            const moduleDescription = moduleDetails?.description || module.description || '';
            return {
              id: module.id,
              name: module.name,
              description: moduleDescription,
              activities: (module.slides || []).map((slide: any) => ({
                id: slide.id,
                name: slide.name,
              })),
            };
          });
          
          const userStatistics = userClassroomStats || statisticsMap.get(classroomId);
          const preservedLevels = classroom.levels || classroomInfo?.levels || undefined;

          return {
            id: classroom.id,
            name: classroom.name,
            description: classroom.description || '',
            // Niveles de la clase (A1, A2, etc.) - importante para determinar subnivel activo
            levels: preservedLevels,
            // Información adicional de la clase
            info: classroomInfo || null,
            // Módulos y actividades
            modules,
            // Estadísticas del usuario en esta clase
            // Si userClassroomStats está disponible, usar esas estadísticas detalladas
            statistics: userStatistics
              ? {
                  timespent: userStatistics.timespent,
                  progress: userStatistics.progress,
                  satisfied: userStatistics.satisfied,
                  course_number: userStatistics.course_number,
                  // Incluir datos adicionales si vienen de userclassroom
                  ...(userClassroomStats && typeof userClassroomStats === 'object' 
                    ? userClassroomStats 
                    : {})
                }
              : undefined,
            // Estadísticas detalladas del usuario para este classroom (si están disponibles)
            user_classroom_statistics: userClassroomStats || undefined,
            // Estadísticas de todos los usuarios en la clase
            classroom_statistics: classroomStats.length > 0 ? classroomStats : undefined,
            // Personas en la clase
            students: students.length > 0 ? students : undefined,
            teachers: teachers.length > 0 ? teachers : undefined,
            admins: admins.length > 0 ? admins : undefined,
          };
        })
      );

      const firstname = user.firstname || '';
      const lastname = user.lastname || '';
      
      // Extraer role del usuario
      // PRIORIDAD 1: Role de LTI (si viene de URL)
      // PRIORIDAD 2: Role del objeto user de Apolearn
      // PRIORIDAD 3: Determinar desde relaciones con classrooms
      // PRIORIDAD 4: Usar role por defecto
      let userRole: string | null = null;
      try {
        // PRIORIDAD 1: Verificar si hay role en datos mínimos de LTI
        const existingData = this.apolearnUserData;
        if (existingData && existingData.role) {
          userRole = existingData.role;
          void userRole;
        } else {
          // PRIORIDAD 2: Intentar obtener role desde el objeto user de Apolearn
          userRole = user.role || user.roles || user.user_role || null;
          
          // Si el role viene como "Learner" o "Instructor", mapearlo
          if (userRole) {
            const roleLower = userRole.toLowerCase();
            if (roleLower.includes('learner') || roleLower.includes('student') || roleLower.includes('estudiante')) {
              userRole = 'student';
            } else if (roleLower.includes('instructor') || roleLower.includes('teacher') || roleLower.includes('docente') || roleLower.includes('profesor')) {
              userRole = 'teacher';
            }
          }
          
          // PRIORIDAD 3: Si no hay role en user, intentar determinarlo desde las relaciones con classrooms
          if (!userRole && classrooms.length > 0) {
            const userId = user.id;
            // Verificar si el usuario es teacher en algún classroom
            const isTeacher = classrooms.some((c) => 
              c.teachers && Array.isArray(c.teachers) && 
              c.teachers.some((t: any) => t.id === userId || t.user_id === userId)
            );
            
            // Verificar si el usuario es student en algún classroom
            const isStudent = classrooms.some((c) => 
              c.students && Array.isArray(c.students) && 
              c.students.some((s: any) => s.id === userId || s.user_id === userId)
            );
            
            if (isTeacher) {
              userRole = 'teacher';
            } else if (isStudent) {
              userRole = 'student';
            }
          }
          
          // PRIORIDAD 4: Si no hay role, determinar según el contexto
          if (!userRole) {
            // Si no hay token de LTI
            if (!hasLtiToken) {
              // Solo usar "Undefined" si hay tache_id en la URL (acceso por URL de actividad)
              if (hasTacheId) {
                userRole = 'Undefined';
              } else {
                // Sin LTI ni tache_id: acceso por formulario de correo; se comporta como estudiante
                userRole = 'student';
              }
            } else {
              // Si hay token LTI pero no se pudo determinar role, usar role por defecto
              const defaultRole = 'Instructor';
              const defaultRoleLower = defaultRole.toLowerCase();
              if (defaultRoleLower.includes('learner') || defaultRoleLower.includes('student') || defaultRoleLower.includes('estudiante')) {
                userRole = 'student';
              } else if (defaultRoleLower.includes('instructor') || defaultRoleLower.includes('teacher') || defaultRoleLower.includes('docente') || defaultRoleLower.includes('profesor')) {
                userRole = 'teacher';
              } else {
                userRole = defaultRole;
              }
            }
          }
        }
        
      } catch {
        // En caso de error, determinar role según el contexto
        if (!hasLtiToken) {
          // Solo usar "Undefined" si hay tache_id en la URL
          if (hasTacheId) {
            userRole = 'Undefined';
          } else {
            userRole = 'student';
          }
        } else {
          // Si hay token LTI pero hay error, usar role por defecto
          const defaultRole = 'Instructor';
          const defaultRoleLower = defaultRole.toLowerCase();
          if (defaultRoleLower.includes('learner') || defaultRoleLower.includes('student')) {
            userRole = 'student';
          } else if (defaultRoleLower.includes('instructor') || defaultRoleLower.includes('teacher')) {
            userRole = 'teacher';
          } else {
            userRole = defaultRole;
          }
        }
      }
      
      // Extraer level desde classrooms[0].levels[0].name
      let userLevel: string | null = null;
      try {
        if (classrooms.length > 0 && classrooms[0].levels && Array.isArray(classrooms[0].levels) && classrooms[0].levels.length > 0) {
          const firstLevel = classrooms[0].levels[0];
          const baseLevelName = firstLevel?.name;
          
          if (baseLevelName) {
            // Extraer solo la parte base (A1, A2, B1, B2, C1, C2, etc.)
            const baseLevelMatch = baseLevelName.match(/^(A\d+|B\d+|C\d+)/i);
            if (baseLevelMatch) {
              const baseLevel = baseLevelMatch[1];
              // Por ahora, usar el primer subnivel disponible (A1.1, A2.1, etc.)
              // El subnivel activo se determinará después basado en el progreso
              const sublevelMap: Record<string, string> = {
                'A1': 'A1.1',
                'A2': 'A2.1',
                'B1': 'B1.1',
                'B2': 'B2.1',
                'C1': 'C1.A',
                'C2': 'C2.A',
              };
              userLevel = sublevelMap[baseLevel] || `${baseLevel}.1`;
            }
          }
        }
      } catch {
        // Level no disponible
      }
      
      const apolearnData: ApolearnUserData = {
        user: {
          id: user.id,
          email: user.email,
          username: user.username,
          firstname: firstname,
          lastname: lastname,
          subscription: {
            current_term_start: user.subscription?.current_term_start || 0,
            current_term_end: user.subscription?.current_term_end || 0,
          },
          is_account_valid: user.subscription?.current_term_end
            ? new Date(user.subscription.current_term_end * 1000) > new Date()
            : false,
        },
        classrooms,
        role: userRole,
        level: userLevel,
      };

      this.apolearnUserData = apolearnData;
      return apolearnData;
    } catch (error: any) {
      console.error('[AuthService] ❌ Error al obtener datos completos:', error.message || error);
      throw error;
    }
  }

  /**
   * Obtener información básica de una clase
   * Endpoint: GET /classrooms/get/{classroom_id}
   */
  async getApolearnClassroom(classroomId: number): Promise<any> {
    const apiEndpoint = this.getApolearnBaseUrl();
    const token = await this.getApolearnToken();

    try {
      const response = await axios.get(
        `${apiEndpoint}/classrooms/get/${classroomId}`,
        {
          params: {
            auth_token: token,
          },
        }
      );

      let result = this.extractResult(response.data);
      return result;
    } catch (error: any) {
      console.error('[AuthService] ❌ Error al obtener información de clase:', error.message || error);
      throw error;
    }
  }

  /**
   * Obtener estudiantes de una clase
   * Endpoint: GET /classrooms/students/{classroom_id}
   */
  async getApolearnClassroomStudents(classroomId: number): Promise<any[]> {
    const apiEndpoint = this.getApolearnBaseUrl();
    const token = await this.getApolearnToken();

    try {
      const response = await axios.get(
        `${apiEndpoint}/classrooms/students/${classroomId}`,
        {
          params: {
            auth_token: token,
          },
        }
      );

      let result = this.extractResult(response.data);
      let students: any[] = [];
      if (Array.isArray(result)) {
        students = result;
      } else if (result && typeof result === 'object') {
        if ('students' in result && Array.isArray(result.students)) {
          students = result.students;
        } else if ('result' in result && Array.isArray(result.result)) {
          students = result.result;
        }
      }
      
      return students;
    } catch (error: any) {
      console.error('[AuthService] ❌ Error al obtener estudiantes:', error.message || error);
      return [];
    }
  }

  /**
   * Obtener profesores de una clase
   * Endpoint: GET /classrooms/teachers/{classroom_id}
   */
  async getApolearnClassroomTeachers(classroomId: number): Promise<any[]> {
    const apiEndpoint = this.getApolearnBaseUrl();
    const token = await this.getApolearnToken();

    try {
      const response = await axios.get(
        `${apiEndpoint}/classrooms/teachers/${classroomId}`,
        {
          params: {
            auth_token: token,
          },
        }
      );

      let result = this.extractResult(response.data);
      let teachers: any[] = [];
      if (Array.isArray(result)) {
        teachers = result;
      } else if (result && typeof result === 'object') {
        if ('teachers' in result && Array.isArray(result.teachers)) {
          teachers = result.teachers;
        } else if ('result' in result && Array.isArray(result.result)) {
          teachers = result.result;
        }
      }
      
      return teachers;
    } catch (error: any) {
      console.error('[AuthService] ❌ Error al obtener profesores:', error.message || error);
      return [];
    }
  }

  /**
   * Obtener administradores de una clase
   * Endpoint: GET /classrooms/admins/{classroom_id}
   */
  async getApolearnClassroomAdmins(classroomId: number): Promise<any[]> {
    const apiEndpoint = this.getApolearnBaseUrl();
    const token = await this.getApolearnToken();

    try {
      const response = await axios.get(
        `${apiEndpoint}/classrooms/admins/${classroomId}`,
        {
          params: {
            auth_token: token,
          },
        }
      );

      // Imprimir respuesta completa de getApolearnClassroomAdmins
      console.log('[AuthService] 📥 RESPUESTA COMPLETA de classrooms/admins/{classroom_id}:', JSON.stringify(response.data, null, 2));
      
      let result = this.extractResult(response.data);
      let admins: any[] = [];
      if (Array.isArray(result)) {
        admins = result;
      } else if (result && typeof result === 'object') {
        if ('admins' in result && Array.isArray(result.admins)) {
          admins = result.admins;
        } else if ('result' in result && Array.isArray(result.result)) {
          admins = result.result;
        }
      }
      
      return admins;
    } catch (error: any) {
      console.error('[AuthService] ❌ Error al obtener administradores:', error.message || error);
      return [];
    }
  }

  /**
   * Obtener estadísticas de una clase específica
   * Endpoint: GET /statistics/classroom/{classroom_id}
   */
  async getApolearnClassroomStatistics(classroomId: number): Promise<any[]> {
    const apiEndpoint = this.getApolearnBaseUrl();
    const token = await this.getApolearnToken();

    try {
      const response = await axios.get(
        `${apiEndpoint}/statistics/classroom/${classroomId}`,
        {
          params: {
            auth_token: token,
          },
        }
      );

      let result = this.extractResult(response.data);
      let statistics: any[] = [];
      if (Array.isArray(result)) {
        statistics = result;
      } else if (result && typeof result === 'object') {
        if ('statistics' in result && Array.isArray(result.statistics)) {
          statistics = result.statistics;
        } else if ('result' in result && Array.isArray(result.result)) {
          statistics = result.result;
        }
      }
      
      return statistics;
    } catch (error: any) {
      console.error('[AuthService] ❌ Error al obtener estadísticas de clase:', error.message || error);
      return [];
    }
  }

  /**
   * Obtener descripción detallada de un módulo desde Apolearn
   * GET /classrooms/module/{id}
   */
  async getApolearnModule(moduleId: number): Promise<any> {
    const apiEndpoint = this.getApolearnBaseUrl();
    const token = await this.getApolearnToken();

    try {
      // Apolearn requiere TANTO api_key COMO auth_token
      // El api_key se manejará en la Lambda desde variables de entorno
      const response = await axios.get(
        `${apiEndpoint}/classrooms/module/${moduleId}`,
        {
          params: {
            auth_token: token,
            // api_key se agregará automáticamente en la Lambda
          },
        }
      );

      // Imprimir respuesta completa de getApolearnModule
      console.log('[AuthService] 📥 RESPUESTA COMPLETA de classrooms/module/{id}:', JSON.stringify(response.data, null, 2));
      
      let result = this.extractResult(response.data);
      
      // El resultado puede ser un objeto con la información del módulo
      return result || null;
    } catch (error: any) {
      console.error('[AuthService] ❌ Error al obtener módulo:', error.message || error);
      return null;
    }
  }

  /**
   * Obtener estadísticas detalladas del usuario para un classroom específico
   * GET /statistics/userclassroom/{id}
   */
  async getApolearnUserClassroomStatistics(classroomId: number): Promise<any> {
    const apiEndpoint = this.getApolearnBaseUrl();
    const token = await this.getApolearnToken();

    try {
      const url = `${apiEndpoint}/statistics/userclassroom/${classroomId}`;
      
      // Apolearn requiere TANTO api_key COMO auth_token
      // El api_key se manejará en la Lambda desde variables de entorno
      const response = await axios.get(url, {
        params: {
          auth_token: token,
          // api_key se agregará automáticamente en la Lambda
        },
      });

      let result = this.extractResult(response.data);
      
      // El resultado puede ser un objeto con las estadísticas detalladas del usuario
      return result || null;
    } catch (error: any) {
      const status = error.response?.status;
      if (status !== 404) {
        console.error('[AuthService] ❌ Error al obtener estadísticas del usuario para el classroom:', error.message || error);
      }
      return null;
    }
  }

  /**
   * Imprimir estadísticas de todos los classroom_ids del usuario
   * Obtiene los datos completos del usuario e imprime las estadísticas de cada classroom
   */
  async printAllClassroomStatistics(): Promise<void> {
    try {
      const config = configService.getConfig();
      const email = config.email;
      const userId = config.userID;

      if (!email && !userId) {
        console.error('[AuthService] ❌ No hay email ni userId para obtener estadísticas');
        return;
      }

      const apolearnData = await this.getApolearnCompleteData(email || '', userId && userId > 0 ? userId : undefined);

      if (!apolearnData?.classrooms?.length) {
        return;
      }
      // Función solo obtiene datos; sin logs locales
    } catch (error: any) {
      console.error('[AuthService] ❌ Error al obtener estadísticas de classrooms:', error.message || error);
      throw error;
    }
  }
}

export const authService = new AuthService();
export default authService;


